import { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from "react-helmet";
import {
    FiArrowRight, FiCode, FiLayers, FiSmartphone, FiGlobe,
    FiShield, FiChevronDown, FiCheck, FiPlay, FiTarget
} from 'react-icons/fi';

const WebDevelopmentPage = () => {
    const [activeTab, setActiveTab] = useState('frontend');
    const [expandedFeature, setExpandedFeature] = useState(null);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const technologies = {
        frontend: ['React', 'Next.js', 'Vue', 'Angular', 'Tailwind CSS', 'TypeScript', 'dotnet'],
        backend: ['Node.js', 'Express', 'Django', 'Ruby on Rails', 'Laravel', 'Spring Boot', 'ASP.net'],
        databases: ['MongoDB', 'PostgreSQL', 'MySQL', 'Firebase', 'Redis', 'Elasticsearch'],
        devops: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Terraform', 'NGINX']
    };

    const currentTechnologies = technologies[activeTab] || [];

    const features = [
        {
            title: "Custom Web Applications",
            icon: <FiCode className="text-3xl" />,
            description: "Tailored solutions designed specifically for your business needs and workflows.",
            details: "From CRM systems to inventory management, we build web apps that streamline operations and boost productivity."
        },
        {
            title: "Responsive Design",
            icon: <FiSmartphone className="text-3xl" />,
            description: "Flawless user experience across all devices and screen sizes.",
            details: "Mobile-first approach ensures your website looks and performs perfectly on smartphones, tablets, and desktops."
        },
        {
            title: "SEO Optimization",
            icon: <FiGlobe className="text-3xl" />,
            description: "Built for visibility with clean code and best practices.",
            details: "Our websites are optimized for search engines from the ground up, helping you rank higher and attract more traffic."
        },
        {
            title: "Performance Focused",
            icon: <FiLayers className="text-3xl" />,
            description: "Lightning-fast load times and smooth interactions.",
            details: "We optimize every aspect for speed, from image compression to code splitting, ensuring exceptional performance."
        },
        {
            title: "Secure Architecture",
            icon: <FiShield className="text-3xl" />,
            description: "Enterprise-grade security protecting your data and users.",
            details: "Implementation of security best practices including HTTPS, data encryption, and regular vulnerability testing."
        }
    ];

    const processSteps = [
        {
            title: "Discovery & Planning",
            description: "We analyze your requirements and create a detailed project roadmap",
            icon: "📋"
        },
        {
            title: "Design & Prototyping",
            description: "Our designers create wireframes and interactive prototypes",
            icon: "🎨"
        },
        {
            title: "Development",
            description: "Our developers build your solution with clean, maintainable code",
            icon: "💻"
        },
        {
            title: "Testing & Quality Assurance",
            description: "Rigorous testing ensures everything works perfectly",
            icon: "🔍"
        },
        {
            title: "Deployment & Launch",
            description: "We handle deployment and ensure a smooth launch",
            icon: "🚀"
        },
        {
            title: "Maintenance & Support",
            description: "Ongoing support to keep your solution running smoothly",
            icon: "🔧"
        }
    ];

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
            <Helmet>
                <title>Web Development Services | Modern, Scalable Websites</title>
                <meta
                    name="description"
                    content="UniTechnoStack builds high-performance websites with modern UI/UX, responsive design, and secure technology stacks to scale your business online."
                />
                <meta
                    name="keywords"
                    content="web development services, responsive websites, custom web applications, frontend development, backend development, UniTechnoStack"
                />
                <meta name="robots" content="index, follow" />
                <link
                    rel="canonical"
                    href="https://www.unitechnostack.com/web-development"
                />

                {/* Open Graph tags */}
                <meta
                    property="og:title"
                    content="Web Development Services | Modern, Scalable Websites"
                />
                <meta
                    property="og:description"
                    content="We create responsive, secure, and modern websites that drive growth for your business."
                />
                <meta
                    property="og:url"
                    content="https://www.unitechnostack.com/webdev"
                />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative py-28 px-4 sm:px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute inset-0 overflow-hidden opacity-20">
                    {[...Array(15)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, -100, 0],
                                x: [0, Math.random() * 80 - 40, 0],
                                rotate: [0, 360],
                                scale: [0.8, 1.2, 0.8]
                            }}
                            transition={{
                                duration: 15 + Math.random() * 15,
                                repeat: Infinity,
                                repeatType: "reverse",
                                ease: "linear"
                            }}
                            className="absolute rounded-lg bg-white/10"
                            style={{
                                width: `${Math.random() * 200 + 50}px`,
                                height: `${Math.random() * 200 + 50}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>

                {/* Grid pattern overlay */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }} />
                </div>

                <div className="max-w-7xl mx-auto relative z-10 mt-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                            <span className="w-2 h-2 bg-blue-300 rounded-full mr-2 animate-pulse"></span>
                            WEB DEVELOPMENT SERVICES
                        </div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
                            Build the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">Digital Future</span> of Your Business
                        </h1>
                        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                            Custom web solutions designed to elevate your brand, engage your audience, and drive business growth
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="flex justify-center gap-4 flex-wrap"
                    >
                        <a href="/quickenquiry">
                            <button className="px-6 py-3 bg-white text-blue-900 rounded-full font-medium hover:bg-gray-100 transition-colors shadow-lg flex items-center">
                                Get a Free Quote <FiArrowRight className="ml-2" />
                            </button>
                        </a>
                        <a href="/portfolio">
                            <button className="px-6 py-3 border border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors">
                                View Our Work
                            </button>
                        </a>
                    </motion.div>

                    {/* Stats Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
                    >
                        {[
                            { value: '50+', label: 'Projects Completed' },
                            { value: '95%', label: 'Client Satisfaction' },
                            { value: '15+', label: 'Expert Developers' },
                            { value: '24/7', label: 'Support Available' }
                        ].map((stat, index) => (
                            <div key={index} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                                <div className="text-2xl font-bold mb-1">{stat.value}</div>
                                <div className="text-sm text-blue-200">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Services Overview */}
            <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">
                        Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Web Solutions</span>
                    </h2>
                    <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                        From simple websites to complex web applications, we deliver exceptional digital experiences
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        {
                            title: "Website Development",
                            description: "Beautiful, functional websites that convert visitors into customers",
                            icon: "🌐"
                        },
                        {
                            title: "Web Applications",
                            description: "Custom solutions tailored to your specific business needs",
                            icon: "💻"
                        },
                        {
                            title: "E-Commerce",
                            description: "Online stores designed to maximize sales and user experience",
                            icon: "🛒"
                        }
                    ].map((service, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all border border-gray-100 group"
                        >
                            <div className="text-4xl mb-4">{service.icon}</div>
                            <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                            <p className="text-gray-600">{service.description}</p>

                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Technology Stack Section */}
            <section className="py-16 px-4 sm:py-20 sm:px-6 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
                {/* Background elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(8)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, Math.random() * 100 - 50],
                                x: [0, Math.random() * 100 - 50],
                                rotate: [0, 360]
                            }}
                            transition={{
                                duration: 20 + Math.random() * 20,
                                repeat: Infinity,
                                repeatType: "reverse",
                                ease: "linear"
                            }}
                            className="absolute rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 opacity-20"
                            style={{
                                width: `${Math.random() * 300 + 100}px`,
                                height: `${Math.random() * 300 + 100}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                                filter: 'blur(40px)'
                            }}
                        />
                    ))}
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Section Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-12 sm:mb-16"
                    >
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium mb-4">
                            OUR TECH STACK
                        </span>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                            Built With <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Cutting-Edge</span> Technologies
                        </h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            We leverage modern tools to deliver high-performance, scalable solutions
                        </p>
                    </motion.div>

                    {/* Technology Selector */}
                    <div className="mb-8 sm:mb-12">
                        {/* Mobile - Select Dropdown */}
                        <div className="md:hidden relative">
                            <select
                                value={activeTab}
                                onChange={(e) => setActiveTab(e.target.value)}
                                className="w-full px-5 py-3 pr-10 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white shadow-sm text-blue-700 font-medium"
                            >
                                {Object.keys(technologies).map((category) => (
                                    <option key={category} value={category}>
                                        {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                <FiChevronDown className="text-blue-500" />
                            </div>
                        </div>

                        {/* Desktop - Pill Tabs */}
                        <div className="hidden md:flex justify-center">
                            <div className="inline-flex bg-gray-100 p-1 rounded-full">
                                {Object.keys(technologies).map((category) => (
                                    <button
                                        key={category}
                                        onClick={() => setActiveTab(category)}
                                        className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${activeTab === category
                                            ? 'bg-white shadow-md text-blue-600'
                                            : 'text-gray-600 hover:text-gray-800'
                                            }`}
                                    >
                                        {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Technology Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                        {currentTechnologies.map((tech, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                whileHover={{
                                    y: -8,
                                    scale: 1.03,
                                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                }}
                                className="bg-white rounded-xl shadow-lg border border-gray-100 hover:border-blue-100 transition-all overflow-hidden group relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <div className="relative p-5 sm:p-6 flex flex-col items-center text-center h-full">
                                    {/* Tech Logo Placeholder */}
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center mb-4 group-hover:from-blue-200 group-hover:to-purple-200 transition-all shadow-inner">
                                        <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                                            {tech.charAt(0)}
                                        </span>
                                    </div>

                                    <h3 className="text-lg font-bold text-gray-800 mb-2">{tech}</h3>
                                    <p className="text-sm text-gray-600 hidden sm:block">
                                        {tech} specialist with 5+ years experience
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Key Features */}
            <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">
                        Why Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Web Development</span> Stands Out
                    </h2>
                    <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                        We combine technical excellence with creative design to deliver exceptional results
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className={`p-6 rounded-2xl transition-all ${expandedFeature === index ? 'bg-white shadow-lg border border-blue-100' : 'bg-gray-50 hover:bg-white hover:shadow-md'}`}
                            onClick={() => setExpandedFeature(expandedFeature === index ? null : index)}
                        >
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-6 text-blue-600 bg-blue-50 p-3 rounded-lg">
                                    {feature.icon}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                    <p className="text-gray-600 mb-2">{feature.description}</p>
                                    {expandedFeature === index && (
                                        <motion.p
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="text-gray-600 pt-2"
                                        >
                                            {feature.details}
                                        </motion.p>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Process Section */}
            <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Development Process</span>
                        </h2>
                        <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                            A structured approach that ensures quality, transparency, and timely delivery
                        </p>
                    </div>

                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-purple-500 hidden md:block"></div>

                        <div className="grid md:grid-cols-2 gap-10 relative">
                            {processSteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className={`flex ${index % 2 === 0 ? 'md:pr-10 md:text-right flex-row-reverse' : 'md:pl-10'} items-start`}
                                >
                                    <div className="flex-1">
                                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                                            <div className="text-3xl mb-3">{step.icon}</div>
                                            <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                                            <p className="text-gray-600">{step.description}</p>
                                        </div>
                                    </div>
                                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full border-4 border-white shadow-xl mx-4 mt-6 hidden md:block"></div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="bg-gradient-to-r from-blue-700 to-purple-800 text-white rounded-2xl p-8 sm:p-10 md:p-12 text-center relative overflow-hidden">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10">
                        <h2 className="text-3xl font-bold mb-4">Ready to Build Your Web Solution?</h2>
                        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                            Let's discuss how we can create a custom web experience for your business
                        </p>
                        <a href="/quickenquiry">
                            <button className="px-8 py-4 bg-white text-blue-900 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl flex items-center mx-auto">
                                Get Started Today
                                <FiArrowRight className="ml-2" />
                            </button>
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WebDevelopmentPage;